import {
    addDays,
    addHours,
    addMinutes,
    differenceInHours,
    endOfDay,
    isBefore,
    isEqual,
    isSameDay,
    max,
    min,
    parseISO,
    roundToNearestMinutes,
    startOfDay,
    startOfHour,
    subDays,
    subHours,
    subMinutes
} from "date-fns";
import { dateFromState, dateToState, startOfNextDay } from "../../Helpers/DateTimeHelper";
import {
    intervalArrayFromState,
    intervalFromState,
    intervalsOverlap,
    intervalToState
} from "../../Helpers/IntervalHelper";
import { TIMELINE_HOURS_PER_PAGE } from "../../Modules/Bookings/Forms/Inputs/AmenityVariableAvailability";


/**
 * Returns a default timeline interval based on an amenity's
 * opening hours
 * @param openingHours
 * @param bookingDate
 * @param selectedInterval
 * @param numHoursInTimeline
 * @returns {{start: string, end: string}}
 */
export const getDefaultTimelineInterval = (openingHours, bookingDate, selectedInterval, numHoursInTimeline = TIMELINE_HOURS_PER_PAGE) => {
    let timelineStart = null;
    bookingDate = dateFromState(bookingDate);

    if (selectedInterval && selectedInterval.start) {
        selectedInterval = intervalFromState(selectedInterval);
        timelineStart = startOfHour(selectedInterval.start);
    } else if (openingHours) {
        // Ensure opening hours is an array of opening intervals
        if (!(openingHours instanceof Array)) {
            openingHours = [openingHours];
        }

        openingHours = intervalArrayFromState(openingHours);

        let currentOpeningPeriod = openingHours[0];

        // Find most recent opening period before the current time
        for (let i in openingHours) {
            let thisOpenPeriod = openingHours[i];
            if (isBefore(thisOpenPeriod.start, bookingDate)) {
                currentOpeningPeriod = thisOpenPeriod;
            }
        }

        if (openingHours[0].hasOwnProperty('start')) {
            var open = dateFromState(currentOpeningPeriod.start);
            var close = dateFromState(currentOpeningPeriod.end);
        } else {
            // TODO - remove deprecated usage of open / close properties
            open = dateFromState(currentOpeningPeriod.open);
            close = dateFromState(currentOpeningPeriod.close);
        }
    } else {
        timelineStart = startOfDay(bookingDate);
    }

    // Don't let timeline start go into previous day
    timelineStart = max([
        startOfDay(bookingDate),
        subHours(timelineStart, 1)
    ]);
    let timelineEnd = addHours(timelineStart, numHoursInTimeline);

    return {
        start: dateToState(timelineStart),
        end: dateToState(timelineEnd)
    }
}

/**
 * Return a default selection of an availability slot for a given timeline
 * with disabled intervals
 * @param bookingDate
 * @param availableIntervals
 * @returns {{start: Date, end: Date}}
 */
export const getDefaultAvailabilitySlot = (bookingDate, availableIntervals) => {
    let currentDateTime = new Date();
    bookingDate = dateFromState(bookingDate);
    availableIntervals = intervalArrayFromState(availableIntervals);

    let dateBoundary = {
        start: startOfDay(bookingDate),
        end: startOfNextDay(bookingDate)
    };

    let clampedIntervals = clampIntervals(availableIntervals, dateBoundary);

    for (var i in clampedIntervals) {
        let thisInterval = clampedIntervals[i];

        if (thisInterval.start > currentDateTime) {
            return {
                start: thisInterval.start,
                end: addMinutes(thisInterval.start, 15)
            }
        }

        if (thisInterval.end > currentDateTime) {
            let timeWithinInterval = roundToNearestMinutes(currentDateTime, {nearestTo: 15});
            if (isBefore(timeWithinInterval, currentDateTime)) {
                timeWithinInterval = addMinutes(timeWithinInterval, 15);
            }
            return {
                start: timeWithinInterval,
                end: addMinutes(timeWithinInterval, 15)
            }
        }
    }

    // If we still don't have an interval, we can assume there isn't a valid
    // interval on the current day. Just set an invalid one and let the user
    // select a valid interval later.
    if (isSameDay(bookingDate, currentDateTime)) {
        var startTime = startOfHour(addHours(currentDateTime, 2));
        // Don't allow default day to cross midnight
        startTime = min([startTime, subMinutes(startOfNextDay(startTime), 15)])
    } else {
        startTime = startOfDay(bookingDate);
    }

    return {
        start: startTime,
        end: addMinutes(startTime, 15),
    }
}


/**
 * Take an array of intervals and return the inverse of those intervals
 * within the given boundary.
 * Ie takes an array of booked intervals within a day then returns an
 * array containing the available intervals
 * @author ChatGPT & JMac - This is the first time JMac got an AI to do the hard work
 * @param withinBoundary
 * @param intervals
 * @returns {*[]}
 */
export const invertIntervals = (intervals, withinBoundary) => {
    intervals.sort((a, b) => a.start - b.start);
    let startBoundary = dateFromState(withinBoundary.start);
    let endBoundary = dateFromState(withinBoundary.end);

    let result = [];
    let threshold = startBoundary;

    for (let i = 0; i < intervals.length; i++) {
        let thisInterval = intervalFromState(intervals[i])
        if (thisInterval.start > threshold && thisInterval.end <= endBoundary) {
            result.push({start: threshold, end: thisInterval.start});
            threshold = thisInterval.end;
        } else if (thisInterval.end > threshold && thisInterval.end <= endBoundary) {
            threshold = max([threshold, thisInterval.end]);
        }
    }
    if (threshold < endBoundary) {
        result.push({start: threshold, end: endBoundary});
    }
    return result;
}

export const findAvailableIntervals = (blockedIntervals, withinBoundary) => {
    return invertIntervals(blockedIntervals, withinBoundary);
}

export const findBlockedIntervals = (availableIntervals, withinBoundary) => {
    return invertIntervals(availableIntervals, withinBoundary);
}

export function mergeIntervals(intervals) {
    intervals = intervalArrayFromState(intervals);
    if (intervals.length <= 1) return intervals;

    intervals.sort((a, b) => a.start - b.start);
    let result = [intervals[0]];

    for (let i = 1; i < intervals.length; i++) {
        let currentInterval = intervals[i];
        let previousInterval = result[result.length - 1];
        if (currentInterval.start <= previousInterval.end) {
            previousInterval.end = max([previousInterval.end, currentInterval.end]);
        } else {
            result.push(currentInterval);
        }
    }
    return result;
}

export function clampIntervals(intervals, boundary) {
    intervals = intervalArrayFromState(intervals);
    boundary = intervalFromState(boundary);

    let clampedIntervals = [];
    for (let i = 0; i < intervals.length; i++) {
        let interval = intervals[i];
        if (!intervalsOverlap(interval, boundary)) {
            continue;
        }
        clampedIntervals.push({
            ...interval,
            start: max([interval.start, boundary.start]),
            end: min([interval.end, boundary.end]),
        });
    }
    return clampedIntervals;
}

/**
 *
 */
export const extendAvailabilityBoundary = (boundary, newDateTime, threshold = null, howCloseToBoundary = 24) => {
    let daysToExtendBy = 2;

    newDateTime = dateFromState(newDateTime);
    boundary = intervalFromState(boundary);

    let diffFromEnd = differenceInHours(boundary.end, newDateTime);

    if (newDateTime > boundary.end
        || Math.abs(diffFromEnd) <= howCloseToBoundary
    ) {
        boundary.end = startOfDay(addDays(newDateTime, daysToExtendBy+1));
    }

    let diffFromStart = differenceInHours(boundary.start, newDateTime);

    if (newDateTime < boundary.start ||
        Math.abs(diffFromStart) <= howCloseToBoundary
    ) {
        boundary.start = startOfDay(subDays(newDateTime, daysToExtendBy));
    }

    // Maybe clamp the acceptable output within a threshold interval
    if (threshold) {
        threshold = intervalFromState(threshold);
        if (threshold.start) {
            boundary.start = max([boundary.start, threshold.start]);
        }
        if (threshold.end) {
            boundary.end = min([boundary.end, threshold.end])
        }
    }

    return intervalToState(boundary);
}

/**
 *
 * @param startDate
 * @param endDate
 * @param $interval
 * @returns {Date[]}
 * @todo Make this more configurable (start/end datetime, interval parameter etc...)
 */
export const generateDateArray = (startDate, endDate = null, $interval = 15, generateDateStrings = false) => {
    startDate = dateFromState(startDate);
    endDate = (endDate)
        ? dateFromState(endDate)
        : endOfDay(startDate);

    let dates = [];
    let currentDate = new Date(startDate.getTime());

    while (currentDate <= endDate) {
        let date = new Date(currentDate.getTime());
        if (generateDateStrings) {
            date = dateToState(date);
        }
        dates.push(date);
        currentDate.setMinutes(currentDate.getMinutes() + $interval);
    }

    return dates;
}

export function getContainedIntervals(intervals1, intervals2) {
    return intervals1.filter(interval1 => {
        return intervals2.some(interval2 => {
            return (
                interval1.start >= interval2.start && interval1.end <= interval2.end
            );
        });
    });
}

export function getOverlappingIntervals (intervals1, intervals2) {
    return intervals1.filter(interval1 => {
        return intervals2.some(interval2 => {
            return (
                (interval1.start >= interval2.start && interval1.start < interval2.end)
                || (interval1.end >= interval2.start && interval1.end < interval2.end)
                || (interval1.start <= interval2.start && interval1.end >= interval2.end)
            );
        });
    });
}

export const getAvailableSlot = (slotStart, slotEnd, availableIntervals) => {
    return availableIntervals.find(current => {
        let currentStart = parseISO(current.start);
        let currentEnd = parseISO(current.end);
        return (isEqual(currentStart, slotStart) && isEqual(currentEnd, slotEnd));
    });
}

export const getEarlierSlot = (selectedInterval, availableIntervals, duration) => {
    return getAvailableSlot(
        subMinutes(parseISO(selectedInterval.start), duration),
        parseISO(selectedInterval.start),
        availableIntervals
    );
}

export const getLaterSlot = (selectedInterval, availableIntervals, duration) => {
    return getAvailableSlot(
        parseISO(selectedInterval.end),
        addMinutes(parseISO(selectedInterval.end), duration),
        availableIntervals
    );
}
